import { el, tel, StatusLine } from "./dom.ts";
import { t, onLangChange } from "./i18n.ts";
import { subscribe, getState, setState } from "./state.ts";
import { createSection, type Section } from "./section.ts";
import { SECTION_ICON } from "./icons.ts";
import { createExportConsole } from "./console-view.ts";
import { createScanSection } from "./scan-panel.ts";
import { scanModule } from "../scan/scan.ts";
import {
    hasDateRange,
    selectsNothing,
    EMPTY_PLAN,
    type ExportPlan,
    type ContainerSelection,
    type DateRange
} from "../export/plan.ts";
import { putRun, clearWritten } from "../storage/idb.ts";
import { deleteTeamsConversations, deleteMailFolders } from "../delete/delete.ts";
import { DELETE_SCOPES } from "../auth/config.ts";
import type { ViewerSource } from "./viewer/source.ts";
import { formatBytes } from "./capability.ts";
import { exportTeams } from "../modules/teams/export.ts";
import { exportMail } from "../modules/mail/export.ts";
import { exportOneDrive } from "../modules/onedrive/export.ts";
import { exportTasks } from "../modules/tasks/export.ts";
import { exportContacts } from "../modules/contacts/export.ts";
import { exportCalendar } from "../modules/calendar/export.ts";
import { exportOneNote } from "../modules/onenote/export.ts";
import { ExportAbortedError } from "../export/abort.ts";
import {
    startOrResumeRun,
    setRunStatus,
    runLogger,
    getCurrentRun,
    forgetCurrentRun,
    type RunLogger
} from "../export/run.ts";
import { writeRunArtifacts } from "../export/artifacts.ts";
import { moduleProgress, totalBytesWritten } from "../storage/idb.ts";
import { InteractionRequiredError, NotSignedInError, acquireTokenInteractive } from "../auth/broker.ts";
import { scopesForModules } from "../auth/scopes.ts";
import { MODULE_SCOPES } from "../auth/config.ts";
import type { AccountInfo } from "@azure/msal-browser";
import { GraphError } from "../graph/client.ts";
import { getMe } from "../graph/me.ts";
import {
    checkModuleReachable,
    estimateMailSize,
    estimateTeamsCount,
    estimateOneDriveSize,
    estimateTasksCount,
    estimateContactsCount,
    estimateCalendarCount,
    estimateOneNoteCount,
    type SizeEstimate
} from "../export/preflight.ts";
import {
    pickDirectory,
    restoreDirectory,
    requestPermission,
    revealDirectory,
    writeFile,
    fileExists,
    PermissionDeniedError,
    PickerCancelledError,
    WriteFailedError
} from "../storage/fsa.ts";
import { runExclusive, ExportLockedError } from "../export/lock.ts";
import { moduleById } from "../modules/registry.ts";
import type { ModuleId } from "../auth/config.ts";

/**
 * Destination folder + one export run for every selected module, as a
 * single panel (ARCHITECTURE.md §15 steps 2/4/6/7).
 *
 * Folder picking used to be its own gate panel ahead of this one. Merging it
 * in means the user sees where the export is going and the button that
 * starts it at the same time, rather than clicking through one more panel
 * to get there.
 *
 * Connection test and per-module preflight checks used to run automatically
 * right after "Approve access", in the setup panel. They now run as the
 * first phase of Start/Resume instead (see runPreflightCheck) — checking
 * "does this actually work" immediately before the thing that needs it to
 * work, every time, rather than once per session somewhere earlier. Preflight
 * stays informational, never a hard gate (ARCHITECTURE.md §10): a module
 * whose check fails here still gets attempted, and any real problem surfaces
 * again, unmissably, in its own export step.
 *
 * One shared run covers every selected module (`startOrResumeRun`), one
 * AbortController governs pause/stop across all of them, and Start and
 * Resume are the same operation: each module's own exportX() is already
 * fully resumable from its own persisted cursors, so simply re-running the
 * whole sequence from the top costs near-zero extra work for any module
 * already finished and picks the interrupted one back up exactly where it
 * stopped.
 *
 * A module's own internal errors (a single chat, folder, or file failing)
 * are already caught inside that module's own export.ts and never reach
 * here. What can reach here is a *module* failing outright — its initial
 * listing call itself erroring — which is caught per-module so one module
 * being unavailable doesn't stop the others from running.
 */

type PanelMode = "idle" | "checking" | "running" | "paused" | "needsConsent";

/** Order the registry itself uses — stable, and future modules slot in for free. */
const RUN_ORDER: readonly ModuleId[] = [
    "teams",
    "mail",
    "onedrive",
    "calendar",
    "contacts",
    "tasks",
    "onenote"
];

const SIZE_ESTIMATORS: Partial<
    Record<ModuleId, (scopes: readonly string[]) => Promise<SizeEstimate>>
> = {
    mail: estimateMailSize,
    teams: estimateTeamsCount,
    onedrive: estimateOneDriveSize,
    tasks: estimateTasksCount,
    contacts: estimateContactsCount,
    calendar: estimateCalendarCount,
    onenote: estimateOneNoteCount
};

const TEST_FILENAME = "o365-exporter-test.txt";

function message(err: unknown): string {
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return t("error.unknown");
}

function moduleLabel(moduleId: ModuleId): string {
    const definition = moduleById(moduleId);
    return definition ? t(definition.labelKey) : moduleId;
}

interface ModuleRunResult {
    items: number;
    bytes: number;
    errors: number;
}

interface RunContext {
    runId: string;
    root: FileSystemDirectoryHandle;
    account: AccountInfo;
    logger: RunLogger;
    signal: AbortSignal;
    /** Bytes already on disk across every module before this whole bulk run began. */
    baselineBytes: number;
    /** Bytes written by modules already finished earlier in this same run. */
    bytesBeforeThisModule: number;
    reportProgress: (currentLabel: string, currentBytes: number, moduleTotalBytes: number) => void;
    log: (message: string, kind: "info" | "ok" | "warn") => void;
    writeArtifacts: () => void;
    /** Scan-step selection + date range for this module, spread into its export call. */
    planArgs: { selection?: ContainerSelection; dateRange?: DateRange };
}

async function runTeamsModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportTeams({
        runId: ctx.runId,
        root: ctx.root,
        selfUserId: ctx.account.localAccountId,
        ...ctx.planArgs,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentChatLabel ?? "", p.currentChatBytes, p.totalBytes);
        },
        onChatComplete: (summary) => {
            if (summary.alreadyComplete && summary.messages === 0) {
                ctx.log(t("console.chatSkipped", { label: summary.label }), "info");
            } else if (summary.alreadyComplete) {
                ctx.log(
                    t("console.chatNewMessages", {
                        label: summary.label,
                        messages: summary.messages,
                        size: formatBytes(summary.bytes)
                    }),
                    "ok"
                );
            } else {
                ctx.log(
                    t("console.chatDone", {
                        label: summary.label,
                        messages: summary.messages,
                        size: formatBytes(summary.bytes)
                    }),
                    "ok"
                );
            }
            ctx.writeArtifacts();
        }
    });
    return { items: result.messages, bytes: result.bytes, errors: result.errors };
}

async function runMailModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportMail({
        runId: ctx.runId,
        root: ctx.root,
        mailboxLabel: ctx.account.username,
        ...ctx.planArgs,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentFolderLabel ?? "", p.currentFolderBytes, p.totalBytes);
        },
        onFolderComplete: (summary) => {
            if (summary.messages === 0) {
                ctx.log(t("console.folderSkipped", { label: summary.label }), "info");
            } else {
                ctx.log(
                    t("console.folderDone", {
                        label: summary.label,
                        messages: summary.messages,
                        size: formatBytes(summary.bytes)
                    }),
                    "ok"
                );
            }
            ctx.writeArtifacts();
        }
    });
    return { items: result.messages, bytes: result.bytes, errors: result.errors };
}

async function runOneDriveModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportOneDrive({
        runId: ctx.runId,
        root: ctx.root,
        ...ctx.planArgs,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentLabel ?? "", p.currentBytes, p.totalBytes);
        },
        onFolderCreated: (path) => {
            ctx.log(t("console.onedriveFolder", { path }), "info");
        },
        onCheckpoint: (checkpoint) => {
            ctx.log(
                t("console.onedriveCheckpoint", {
                    files: checkpoint.files,
                    folders: checkpoint.folders,
                    size: formatBytes(checkpoint.bytes)
                }),
                "info"
            );
            ctx.writeArtifacts();
        }
    });
    return { items: result.files, bytes: result.bytes, errors: result.errors };
}

async function runTasksModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportTasks({
        runId: ctx.runId,
        root: ctx.root,
        ...ctx.planArgs,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentLabel ?? "", 0, p.totalBytes);
        },
        onListComplete: (summary) => {
            if (summary.tasks === 0) {
                ctx.log(t("console.taskListSkipped", { label: summary.label }), "info");
            } else {
                ctx.log(
                    t("console.taskListDone", {
                        label: summary.label,
                        tasks: summary.tasks,
                        size: formatBytes(summary.bytes)
                    }),
                    "ok"
                );
            }
            ctx.writeArtifacts();
        }
    });
    return { items: result.tasks, bytes: result.bytes, errors: result.errors };
}

async function runContactsModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportContacts({
        runId: ctx.runId,
        root: ctx.root,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentLabel ?? "", 0, p.totalBytes);
        }
    });
    return { items: result.contacts, bytes: result.bytes, errors: result.errors };
}

async function runCalendarModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportCalendar({
        runId: ctx.runId,
        root: ctx.root,
        ...ctx.planArgs,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentLabel ?? "", p.currentBytes, p.totalBytes);
        },
        onCalendarComplete: (summary) => {
            if (summary.events === 0) {
                ctx.log(t("console.calendarSkipped", { label: summary.label }), "info");
            } else {
                ctx.log(
                    t("console.calendarDone", {
                        label: summary.label,
                        events: summary.events,
                        size: formatBytes(summary.bytes)
                    }),
                    "ok"
                );
            }
            ctx.writeArtifacts();
        }
    });
    return { items: result.events, bytes: result.bytes, errors: result.errors };
}

async function runOneNoteModule(ctx: RunContext): Promise<ModuleRunResult> {
    const result = await exportOneNote({
        runId: ctx.runId,
        root: ctx.root,
        ...ctx.planArgs,
        logger: ctx.logger,
        signal: ctx.signal,
        onProgress: (p) => {
            ctx.reportProgress(p.currentLabel ?? "", 0, p.totalBytes);
        },
        onPageComplete: (summary) => {
            ctx.log(
                t("console.onenotePageDone", { label: summary.label, size: formatBytes(summary.bytes) }),
                "ok"
            );
            ctx.writeArtifacts();
        }
    });
    return { items: result.pages, bytes: result.bytes, errors: result.errors };
}

const RUNNERS: Partial<Record<ModuleId, (ctx: RunContext) => Promise<ModuleRunResult>>> = {
    teams: runTeamsModule,
    mail: runMailModule,
    onedrive: runOneDriveModule,
    tasks: runTasksModule,
    contacts: runContactsModule,
    calendar: runCalendarModule,
    onenote: runOneNoteModule
};

export interface ExportPanelDeps {
    source: ViewerSource;
    openViewer: (moduleId: ModuleId) => void;
}

export function createExportPanel(deps: ExportPanelDeps): Section {
    const panel = createSection({
        id: "panel-export",
        titleKey: "export.section",
        introKey: "export.intro",
        icon: SECTION_ICON.export
    });

    /* ---- destination folder -------------------------------------------- */

    let folderHandle: FileSystemDirectoryHandle | null = null;

    const folderStatus = new StatusLine();
    const writtenStatus = new StatusLine();

    const pickButton = tel("button", "storage.pickFolder", { type: "button", class: "primary" });
    const reconnectButton = tel("button", "storage.reconnect", { type: "button", class: "primary" });
    const revealButton = tel("button", "storage.showFolder", { type: "button" });
    const testButton = tel("button", "storage.writeTest", { type: "button" });

    const folderActions = el("div", { class: "actions" }, [
        pickButton,
        reconnectButton,
        revealButton,
        testButton
    ]);

    const showWritten = async () => {
        const total = await totalBytesWritten();
        writtenStatus.set(t("storage.written", { size: formatBytes(total) }), "muted");
    };

    let currentFolderState: "none" | "granted" | "prompt" = "none";

    const renderFolder = (folderState: "none" | "granted" | "prompt") => {
        currentFolderState = folderState;
        const name = folderHandle?.name ?? "";

        pickButton.hidden = folderState !== "none";
        reconnectButton.hidden = folderState !== "prompt";
        revealButton.hidden = folderState !== "granted";
        testButton.hidden = folderState !== "granted";
        writtenStatus.node.hidden = folderState !== "granted";

        if (folderState !== "granted") {
            // Export modules must never write through a handle whose permission
            // is unconfirmed.
            setState({ folderHandle: null });
        }

        if (folderState === "granted") {
            folderStatus.set(t("storage.chosen", { name }), "ok");
        } else if (folderState === "prompt") {
            folderStatus.set(t("storage.reconnectNeeded", { name }), "warn");
        } else {
            folderStatus.set(t("storage.none"), "muted");
        }
    };

    pickButton.addEventListener("click", async () => {
        pickButton.disabled = true;
        try {
            folderHandle = await pickDirectory();
            setState({ folderName: folderHandle.name, folderHandle });
            renderFolder("granted");
            // A different destination means item/cursor records from any
            // previous run no longer describe what's on disk here.
            forgetCurrentRun();
            void showWritten();
        } catch (err) {
            if (err instanceof PickerCancelledError) {
                folderStatus.set(t("storage.cancelled"), "warn");
            } else {
                folderStatus.set(t("storage.error", { message: message(err) }), "err");
            }
        } finally {
            pickButton.disabled = false;
        }
    });

    reconnectButton.addEventListener("click", async () => {
        if (!folderHandle) {
            return;
        }
        reconnectButton.disabled = true;
        try {
            await requestPermission(folderHandle);
            setState({ folderName: folderHandle.name, folderHandle });
            renderFolder("granted");
            void showWritten();
        } catch (err) {
            if (err instanceof PermissionDeniedError) {
                folderStatus.set(t("storage.permissionDenied"), "err");
            } else {
                folderStatus.set(t("storage.error", { message: message(err) }), "err");
            }
        } finally {
            reconnectButton.disabled = false;
        }
    });

    revealButton.addEventListener("click", async () => {
        if (!folderHandle) {
            return;
        }
        revealButton.disabled = true;
        try {
            await revealDirectory(folderHandle);
        } catch (err) {
            folderStatus.set(t("storage.error", { message: message(err) }), "err");
        } finally {
            revealButton.disabled = false;
        }
    });

    testButton.addEventListener("click", async () => {
        if (!folderHandle) {
            return;
        }
        testButton.disabled = true;
        try {
            const body = `M365 Personal Data Exporter — write test\n${new Date().toISOString()}\n`;
            const bytes = await writeFile(folderHandle, TEST_FILENAME, body);
            folderStatus.set(t("storage.testWritten", { name: TEST_FILENAME, bytes }), "ok");
            void showWritten();
        } catch (err) {
            folderStatus.set(t("storage.error", { message: message(err) }), "err");
        } finally {
            testButton.disabled = false;
        }
    });

    /* ---- scan step (choose exactly what to export) --------------------- */

    /**
     * Deletes the selected containers of a type from the live Microsoft account
     * (not the local export). Gated behind a typed "DELETE" confirmation and a
     * one-time consent for the write scope. consoleView/status are referenced
     * lazily — this only runs on a user click, long after they exist.
     */
    const deleteFromMicrosoft = async (moduleId: ModuleId, containerIds: readonly string[]): Promise<void> => {
        if (moduleId !== "teams" && moduleId !== "mail") {
            return;
        }
        const label = moduleLabel(moduleId);
        if (containerIds.length === 0) {
            consoleView.log(t("delete.nothingSelected", { module: label }), "warn");
            return;
        }

        const typed = window.prompt(t("delete.prompt", { module: label, count: containerIds.length }));
        if ((typed ?? "").trim().toUpperCase() !== "DELETE") {
            consoleView.log(t("delete.cancelled"), "info");
            return;
        }

        try {
            await acquireTokenInteractive([...DELETE_SCOPES[moduleId]]);
        } catch (err) {
            consoleView.log(t("delete.consentFailed", { message: message(err) }), "err");
            return;
        }

        const deleteController = new AbortController();
        consoleView.log(t("delete.started", { module: label }), "warn");
        status.set(t("delete.progress", { count: 0 }), "warn");
        const cb = {
            log: (msg: string, kind: "info" | "ok" | "warn" | "err") => consoleView.log(msg, kind),
            onProgress: (n: number) => status.set(t("delete.progress", { count: n }), "warn"),
            signal: deleteController.signal
        };

        try {
            const result =
                moduleId === "teams"
                    ? await deleteTeamsConversations(containerIds, getState().account?.localAccountId, cb)
                    : await deleteMailFolders(containerIds, cb);

            consoleView.log(
                moduleId === "teams"
                    ? t("delete.teamsDone", { deleted: result.deleted, skipped: result.skipped })
                    : t("delete.mailDone", { deleted: result.deleted, folders: result.foldersRemoved }),
                "ok"
            );
            if (result.errors > 0) {
                consoleView.log(t("delete.withErrors", { count: result.errors }), "warn");
            }
            status.set(t("delete.done"), "ok");
        } catch (err) {
            const detail = err instanceof GraphError ? `${err.status} ${err.code}` : message(err);
            consoleView.log(t("delete.error", { message: detail }), "err");
            status.set(t("delete.error", { message: detail }), "err");
        }
    };

    const scanSection = createScanSection({
        runScan: (moduleId, signal) =>
            scanModule(moduleId, {
                ...(getState().account?.localAccountId ? { selfUserId: getState().account!.localAccountId } : {}),
                ...(signal ? { signal } : {})
            }),
        source: deps.source,
        openViewer: deps.openViewer,
        deleteFromMicrosoft
    });

    /* ---- run controls + pre-export check + console ---------------------- */

    const status = new StatusLine();
    const consoleView = createExportConsole();

    const checkingLabel = el("span", { class: "checking-label" }, [t("export.checking")]);
    const checkingBarFill = el("div", { class: "progress-bar-fill" });
    const checkingBarTrack = el("div", { class: "progress-bar" }, [checkingBarFill]);
    const checkingList = el("ul", { class: "preflight-list" });
    const checkingSection = el("div", { class: "checking-section" }, [
        checkingLabel,
        checkingBarTrack,
        checkingList
    ]);
    checkingSection.hidden = true;

    const startButton = tel("button", "export.start", { type: "button", class: "primary" });
    const resumeButton = tel("button", "export.resume", { type: "button", class: "primary" });
    const grantButton = tel("button", "export.grantAccess", { type: "button", class: "primary" });
    const pauseButton = tel("button", "export.pause", { type: "button" });
    const stopButton = tel("button", "export.stop", { type: "button" });

    panel.body.append(
        tel("h3", "storage.section", { class: "subsection-heading" }),
        folderActions,
        folderStatus.node,
        writtenStatus.node,
        // Static, not a computed check: no browser API reports free space on the
        // chosen drive, so the user has to be the one to confirm it.
        tel("p", "storage.freeSpace", { class: "notice" }),
        el("hr", { class: "panel-divider" }),
        tel("h3", "scan.heading", { class: "subsection-heading" }),
        tel("p", "scan.intro", { class: "intro" }),
        scanSection.root,
        el("hr", { class: "panel-divider" }),
        tel("h3", "export.runHeading", { class: "subsection-heading" }),
        el("div", { class: "actions" }, [startButton, resumeButton, grantButton, pauseButton, stopButton]),
        checkingSection,
        status.node,
        consoleView.root
    );

    let mode: PanelMode = "idle";
    let controller: AbortController | null = null;
    let abortIntent: "pause" | "stop" | null = null;

    const applyMode = (next: PanelMode) => {
        mode = next;
        const state = getState();
        const ready = state.account !== null && state.folderHandle !== null;

        startButton.hidden = next !== "idle";
        resumeButton.hidden = next !== "paused";
        grantButton.hidden = next !== "needsConsent";
        pauseButton.hidden = next !== "running";
        stopButton.hidden = next !== "running";
        checkingSection.hidden = next !== "checking";

        startButton.disabled = !ready;
        resumeButton.disabled = !ready;
    };

    const restoreProgress = async () => {
        const run = await getCurrentRun();
        if (!run || run.modules.length === 0) {
            return;
        }

        let bytes = 0;
        let done = 0;
        const perModule: { moduleId: ModuleId; done: number; bytes: number }[] = [];
        for (const moduleId of run.modules) {
            const progress = await moduleProgress(run.runId, moduleId);
            bytes += progress.bytes;
            done += progress.done;
            if (progress.done > 0) {
                perModule.push({ moduleId, done: progress.done, bytes: progress.bytes });
            }
        }
        if (done === 0) {
            return;
        }

        consoleView.setStats({ allTimeBytes: bytes, sessionBytes: 0 });
        consoleView.log(
            t("console.exportRestored", { items: done, size: formatBytes(bytes) }),
            "info"
        );
        for (const m of perModule) {
            consoleView.log(
                t("console.restoredModule", {
                    module: moduleLabel(m.moduleId),
                    items: m.done,
                    size: formatBytes(m.bytes)
                }),
                "info"
            );
        }

        if (run.status !== "completed") {
            applyMode("paused");
            status.set(t("export.incomplete"), "warn");
        } else {
            status.set(t("export.done"), "ok");
        }
    };

    /**
     * Confirms the connection actually works, then checks each selected
     * module's own scope against this tenant — the same two checks that used
     * to run automatically right after "Approve access", now run here
     * instead, immediately before the thing they are checking matters.
     * Returns false when a real blocker (needs re-consent, not signed in, no
     * connection at all) means starting the export would just fail
     * immediately anyway; per-module preflight results never return false —
     * they are informational only (ARCHITECTURE.md §10).
     */
    const runPreflightCheck = async (selected: readonly ModuleId[]): Promise<boolean> => {
        checkingList.replaceChildren();
        checkingBarFill.style.width = "0%";
        checkingLabel.textContent = t("export.checkingConnection");

        try {
            await getMe({
                onRetry: ({ delayMs }) => {
                    status.set(t("graph.throttled", { seconds: Math.ceil(delayMs / 1000) }), "warn");
                }
            });
        } catch (err) {
            if (err instanceof InteractionRequiredError) {
                applyMode("needsConsent");
                status.set(t("export.permissionNeeded"), "warn");
            } else if (err instanceof NotSignedInError) {
                applyMode("idle");
                status.set(t("auth.notSignedIn"), "warn");
            } else {
                const detail = err instanceof GraphError ? `${err.status} ${err.code}` : message(err);
                applyMode("idle");
                status.set(t("export.error", { message: detail }), "err");
            }
            return false;
        }

        status.clear();

        for (let i = 0; i < selected.length; i += 1) {
            const moduleId = selected[i];
            if (!moduleId) {
                continue;
            }
            checkingLabel.textContent = t("export.checkingModule", {
                done: i,
                total: selected.length,
                module: moduleLabel(moduleId)
            });
            checkingBarFill.style.width = `${Math.round((i / selected.length) * 100)}%`;

            const statusText = el("span", { class: "preflight-status" }, [t("preflight.checking")]);
            checkingList.append(
                el("li", { class: "preflight-row" }, [
                    el("span", { class: "module-name" }, [moduleLabel(moduleId)]),
                    statusText
                ])
            );

            const scopes = MODULE_SCOPES[moduleId];
            try {
                const reach = await checkModuleReachable(moduleId, scopes);
                const estimator = SIZE_ESTIMATORS[moduleId];
                const estimate = estimator ? await estimator(scopes) : null;

                const parts = [
                    reach.ok
                        ? t("preflight.reachable")
                        : t("preflight.blocked", { error: reach.error ?? "" })
                ];
                if (estimate) {
                    if (estimate.items !== undefined && estimate.bytes !== undefined) {
                        parts.push(
                            t("preflight.sizeKnown", {
                                items: estimate.items,
                                size: formatBytes(estimate.bytes)
                            })
                        );
                    } else if (estimate.bytes !== undefined) {
                        parts.push(t("preflight.sizeBytesOnly", { size: formatBytes(estimate.bytes) }));
                    } else if (estimate.items !== undefined) {
                        parts.push(t("preflight.sizeUnknown", { items: estimate.items }));
                    }
                }
                statusText.textContent = parts.join(" · ");
                statusText.className = `preflight-status ${reach.ok ? "ok" : "warn"}`;
            } catch (err) {
                statusText.textContent = t("preflight.error", {
                    message: err instanceof Error ? err.message : String(err)
                });
                statusText.className = "preflight-status err";
            }
        }

        checkingBarFill.style.width = "100%";
        checkingLabel.textContent = t("export.checkingDone");
        return true;
    };

    const runExport = async (): Promise<void> => {
        const state = getState();
        if (!state.folderHandle) {
            status.set(t("export.noFolder"), "warn");
            return;
        }
        if (!state.account) {
            status.set(t("auth.notSignedIn"), "warn");
            return;
        }
        if (state.selectedModules.length === 0) {
            status.set(t("export.noModules"), "warn");
            return;
        }

        const account = state.account;
        const root = state.folderHandle;
        const selected = RUN_ORDER.filter((id) => state.selectedModules.includes(id));

        applyMode("checking");
        status.clear();
        const checkedOut = await runPreflightCheck(selected);
        if (!checkedOut) {
            return;
        }

        controller = new AbortController();
        const signal = controller.signal;
        abortIntent = null;
        applyMode("running");
        status.clear();

        // If the destination was emptied since the last run (its manifest is
        // gone), the IndexedDB checkpoints are stale — they'd make every item
        // "already done" and skip the whole thing, writing nothing to the now-
        // empty folder. Detect that and reset, so deleting the folder gives a
        // genuine fresh export rather than a silent no-op.
        const priorRun = await getCurrentRun();
        if (priorRun && !(await fileExists(root, "export.json"))) {
            forgetCurrentRun();
            await clearWritten();
            consoleView.log(t("console.folderEmptied"), "warn");
        }

        const run = await startOrResumeRun(selected);
        const logger = runLogger(run.runId, "app");
        // Write the manifest immediately so it exists as the "this folder holds
        // an export" marker even if the run is paused before a module finishes.
        await writeRunArtifacts(root, run.runId, selected);

        // The plan comes from the Scan step if it was used this session; on a
        // resume where the user didn't re-scan, fall back to the plan stored on
        // the run so the same selection/date filter is honoured.
        let plan: ExportPlan;
        if (scanSection.wasScanned()) {
            plan = scanSection.getPlan();
            await putRun({ ...run, options: { ...run.options, plan } });
        } else {
            plan = (run.options["plan"] as ExportPlan | undefined) ?? EMPTY_PLAN;
        }
        for (const [moduleId, range] of Object.entries(plan.dateRanges)) {
            if (range && hasDateRange(range)) {
                consoleView.log(
                    t("console.dateFilter", {
                        module: moduleLabel(moduleId as ModuleId),
                        after: range.after ?? "…",
                        before: range.before ?? "…"
                    }),
                    "info"
                );
            }
        }

        let baselineBytes = 0;
        for (const moduleId of selected) {
            baselineBytes += (await moduleProgress(run.runId, moduleId)).bytes;
        }
        consoleView.setStats({
            sessionBytes: 0,
            allTimeBytes: baselineBytes,
            itemsDone: 0,
            itemsTotal: selected.length
        });
        consoleView.log(t("console.started"), "info");

        let sessionBytesSoFar = 0;
        let totalErrors = 0;
        let moduleIndex = 0;

        try {
            await runExclusive(async () => {
                for (const moduleId of selected) {
                    throwIfAborted(signal);

                    const label = moduleLabel(moduleId);
                    consoleView.log(t("export.moduleStarting", { module: label }), "info");
                    consoleView.setStats({
                        currentLabel: label,
                        currentBytes: 0,
                        itemsDone: moduleIndex,
                        itemsTotal: selected.length
                    });

                    const runner = RUNNERS[moduleId];
                    if (!runner) {
                        // Selected but not yet built — the module checkbox is
                        // disabled for anything unavailable, so this should
                        // not be reachable; skip gracefully rather than fail
                        // the whole run if it somehow is.
                        consoleView.log(t("export.moduleUnavailable", { module: label }), "warn");
                        moduleIndex += 1;
                        continue;
                    }

                    const moduleSelection = plan.selection[moduleId];
                    if (selectsNothing(moduleSelection)) {
                        // The Scan step deselected everything in this module.
                        consoleView.log(t("console.moduleDeselected", { module: label }), "info");
                        moduleIndex += 1;
                        continue;
                    }

                    // Only Teams and Mail ever carry a date range in the plan.
                    const moduleDateRange = plan.dateRanges[moduleId];
                    const planArgs: { selection?: ContainerSelection; dateRange?: DateRange } = {
                        ...(moduleSelection ? { selection: moduleSelection } : {}),
                        ...(moduleDateRange && hasDateRange(moduleDateRange) ? { dateRange: moduleDateRange } : {})
                    };

                    const bytesBeforeThisModule = sessionBytesSoFar;

                    try {
                        const result = await runner({
                            runId: run.runId,
                            root,
                            account,
                            planArgs,
                            logger: runLogger(run.runId, moduleId),
                            signal,
                            baselineBytes,
                            bytesBeforeThisModule,
                            reportProgress: (currentLabel, currentBytes, moduleTotalBytes) => {
                                const sessionBytes = bytesBeforeThisModule + moduleTotalBytes;
                                consoleView.setStats({
                                    currentLabel: currentLabel ? `${label}: ${currentLabel}` : label,
                                    currentBytes,
                                    sessionBytes,
                                    allTimeBytes: baselineBytes + sessionBytes,
                                    itemsDone: moduleIndex,
                                    itemsTotal: selected.length
                                });
                            },
                            log: (msg, kind) => consoleView.log(msg, kind),
                            writeArtifacts: () => void writeRunArtifacts(root, run.runId, selected)
                        });

                        sessionBytesSoFar += result.bytes;
                        totalErrors += result.errors;
                        consoleView.log(
                            t("export.moduleFinished", {
                                module: label,
                                items: result.items,
                                size: formatBytes(result.bytes)
                            }),
                            "ok"
                        );
                    } catch (err) {
                        if (
                            err instanceof ExportAbortedError ||
                            err instanceof InteractionRequiredError ||
                            err instanceof NotSignedInError
                        ) {
                            throw err;
                        }
                        // This module failed outright (its listing call, not
                        // one item within it) — logged, the rest continue.
                        totalErrors += 1;
                        const detail = err instanceof GraphError ? err.toLogString() : message(err);
                        await logger.error(`module ${moduleId} failed outright: ${detail}`);
                        consoleView.log(
                            t("export.moduleFailed", { module: label, message: detail }),
                            "err"
                        );
                    }

                    moduleIndex += 1;
                }
            });

            await setRunStatus(run.runId, "completed");
            await writeRunArtifacts(root, run.runId, selected);

            // Every module finished — the loop only ever set itemsDone to the
            // index of the module *currently* running, so without this the bar
            // rests at "6 of 7" after the 7th completes.
            consoleView.setStats({
                currentLabel: null,
                itemsDone: selected.length,
                itemsTotal: selected.length
            });

            consoleView.log(
                t("console.exportFinished", {
                    size: formatBytes(baselineBytes + sessionBytesSoFar)
                }),
                "ok"
            );
            if (totalErrors > 0) {
                consoleView.log(t("export.withErrors", { count: totalErrors }), "warn");
            }

            // The export just rewrote the on-disk indexes; refresh the Browse
            // counts, which no state change would otherwise trigger.
            void scanSection.refresh();

            applyMode("idle");
            status.set(t("export.done"), "ok");
        } catch (err) {
            if (err instanceof ExportLockedError) {
                consoleView.log(t("console.locked"), "warn");
                applyMode("paused");
                status.set(t("export.locked"), "warn");
                return;
            }

            await setRunStatus(run.runId, "paused");
            await writeRunArtifacts(root, run.runId, selected);
            // Modules that finished before the interruption wrote their indexes.
            void scanSection.refresh();

            if (err instanceof ExportAbortedError) {
                const stopped = abortIntent === "stop";
                consoleView.log(stopped ? t("console.stopped") : t("console.paused"), "warn");
                applyMode(stopped ? "idle" : "paused");
                status.set(stopped ? t("export.stopped") : t("export.paused"), "warn");
            } else if (err instanceof InteractionRequiredError) {
                consoleView.log(t("console.consentNeeded"), "warn");
                applyMode("needsConsent");
                status.set(t("export.permissionNeeded"), "warn");
            } else if (err instanceof NotSignedInError) {
                applyMode("idle");
                status.set(t("auth.notSignedIn"), "warn");
            } else if (err instanceof WriteFailedError) {
                await logger.error(
                    `write failed: ${err.cause instanceof Error ? err.cause.message : String(err.cause)}`
                );
                consoleView.log(t("export.writeFailed"), "err");
                applyMode("paused");
                status.set(t("export.writeFailed"), "err");
            } else {
                const detail = err instanceof GraphError ? `${err.status} ${err.code}` : message(err);
                consoleView.log(t("export.error", { message: detail }), "err");
                applyMode("paused");
                status.set(t("export.error", { message: detail }), "err");
            }
        } finally {
            controller = null;
        }
    };

    function throwIfAborted(signal: AbortSignal): void {
        if (signal.aborted) {
            throw new ExportAbortedError();
        }
    }

    startButton.addEventListener("click", () => void runExport());
    resumeButton.addEventListener("click", () => void runExport());

    pauseButton.addEventListener("click", () => {
        abortIntent = "pause";
        controller?.abort();
        pauseButton.disabled = true;
    });

    stopButton.addEventListener("click", () => {
        abortIntent = "stop";
        controller?.abort();
        stopButton.disabled = true;
    });

    grantButton.addEventListener("click", async () => {
        grantButton.disabled = true;
        try {
            await acquireTokenInteractive(scopesForModules(getState().selectedModules));
            void runExport();
        } catch (err) {
            status.set(t("export.error", { message: message(err) }), "err");
        } finally {
            grantButton.disabled = false;
        }
    });

    const renderRunStatus = (state: ReturnType<typeof getState>) => {
        const ready = state.account !== null && state.folderHandle !== null && state.selectedModules.length > 0;
        const busy = mode === "running" || mode === "checking";

        startButton.disabled = !ready || busy;
        resumeButton.disabled = !ready || busy;
        pauseButton.disabled = false;
        stopButton.disabled = false;

        if (mode === "idle" || mode === "paused") {
            if (state.account === null) {
                status.set(t("auth.notSignedIn"), "muted");
            } else if (state.selectedModules.length === 0) {
                status.set(t("export.noModules"), "muted");
            } else if (state.folderHandle === null) {
                status.set(t("export.noFolder"), "muted");
            } else if (mode === "paused") {
                status.set(t("export.incomplete"), "warn");
            } else if (status.node.classList.contains("muted")) {
                status.clear();
            }
        }

        panel.setState(mode === "idle" && ready, ready ? t("export.readySummary") : t("export.notReadySummary"));
    };

    subscribe(renderRunStatus);
    onLangChange(() => {
        renderRunStatus(getState());
        renderFolder(currentFolderState);
        if (currentFolderState === "granted") {
            void showWritten();
        }
    });

    // Restore a folder chosen in an earlier session. Deliberately does not
    // prompt — re-granting permission needs a user gesture (§7), which is
    // what the reconnect button is for.
    void (async () => {
        const restored = await restoreDirectory();
        if (restored) {
            folderHandle = restored.handle;
            if (restored.permission === "granted") {
                setState({ folderName: folderHandle.name, folderHandle });
            }
            renderFolder(restored.permission === "granted" ? "granted" : "prompt");
            void showWritten();
        } else {
            renderFolder("none");
        }
    })();

    applyMode("idle");
    void restoreProgress();

    return panel;
}
